import React, { useState } from 'react';
import { FormControl } from 'react-bootstrap';
import styled from 'styled-components';
import { Eye, EyeOff } from 'lucide-react';

// Define types for the props
interface GenericInputWithEyeProps {
  label: string;  // Label text for the input field
  value: string;  // The value of the input
  onChange: (value: string) => void;  // Change handler
  type?: string;  // The type of the input (e.g., 'text', 'password', etc.)
  placeholder?: string;  // Placeholder text for the input field
}

// Styled component for the input container
const InputWrapper = styled.div`
  width: 100%;  // Make the wrapper take up full width
  min-width: 200px;  // Set a minimum width for larger screens
  margin-bottom: 1rem;  // Space between inputs

  @media (max-width: 576px) {
    min-width: 100%;  // Full width on mobile devices
  }
`;

// Styled component for the input label
const InputLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #000;  // Black text for the label
  margin-bottom: 0.5rem;
  display: block;  // Make sure label appears above the input
`;

// Styled component for the input field (neutral style)
const StyledInput = styled(FormControl)`
  width: 100%;  // Make the input span full width
  padding: 10px 12px;  // Adjust padding for consistency on mobile
  border: 1px solid #ccc;  // Subtle border
  color: #000;  // Set text color to black
  box-shadow: none;  // Remove shadow for a more neutral design

  &:focus {
    border-color: #aaa;  // Slightly darker border on focus
    box-shadow: none;  // Remove default focus shadow
  }

  &:hover {
  --  background-color: #e9ecef;  // Slight change on hover for user interaction feedback
  }
`;

// Styled component for the eye icon (password toggle button)
const EyeIcon = styled.div`
  position: absolute;  // Position the eye icon inside the input field
  right: 10px;  // Align to the right
  top: 50%;  // Vertically center it
  transform: translateY(-50%);  // Adjust the centering
  cursor: pointer;  // Make it clickable
  color: #777;  // Neutral color for the eye icon
  font-size: 1.2rem;  // Slightly larger icon size

  &:hover {
    color: #000;  // Change color on hover for better interaction feedback
  }
`;

const GenericInputWithEye: React.FC<GenericInputWithEyeProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}) => {
  const [showPassword, setShowPassword] = useState(false);  // State for password visibility toggle

  // Function to toggle password visibility
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  return (
    <InputWrapper>
      {/* Input Label */}
      <InputLabel>{label}</InputLabel>

      {/* Input Field */}
      <div style={{ position: 'relative' }}>
        <StyledInput
          type={showPassword ? 'text' : type}  // Toggle between 'text' and 'password'
          value={value}
          onChange={(e: any) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {/* Eye Icon for toggling password visibility */}
        {type === 'password' && (
          <EyeIcon onClick={togglePasswordVisibility}>
            {showPassword ? <EyeOff /> : <Eye />}
          </EyeIcon>
        )}
      </div>
    </InputWrapper>
  );
};

export default GenericInputWithEye;
